import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Space,
  Checkbox,
  Form,
  Radio,
  Divider,
  Row,
  Col,
  Progress,
  message,
  Alert,
  Typography,
} from 'antd';
import { DownloadOutlined, WarningOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import useBenchmarkExport from './hooks/useBenchmarkExport';
import { ContentArea } from './styled';
import {
  isIntervalGreaterThanADaySelector,
  kpiDimensionMetricsSelector,
  filterSelector,
} from '@store/overviewDashboard/base/selectors';
import {
  ovbDeviceMetricsEnabledSelector,
  advanceBenchmarkSelector,
  benchmarkSelector,
} from '@store/common/selectors';
import { supportedMetricsSelector } from '@store/overviewDashboard/benchmark/selectors';
import { dimensionsListSelector } from '@store/common/dimensionList/selectors';
import {
  getFilterInfo,
  getBenchmarkSupportedDimensions,
  getKpiMetricList,
  getBenchmarkSupportedMetrics,
  getInitialDimensions,
} from './utils';
import { MAX_DIMENSIONS_ALLOWED, MESSAGES, BENCHMARK_TYPES, PERCENTILE_TYPES } from './constants';

const { Text } = Typography;

interface BenchmarkExportDrawerProps {
  visible: boolean;
  onClose: () => void;
  currentShareId: string;
  pageType: number;
  onFooterRender?: (footer: React.ReactNode) => void;
}

const BenchmarkExportDrawer: React.FC<BenchmarkExportDrawerProps> = ({
  visible,
  onClose,
  onFooterRender,
}) => {
  const [form] = Form.useForm();
  const showImpactedDevices = useSelector(ovbDeviceMetricsEnabledSelector);
  const isIntervalGreaterThanADay = useSelector(isIntervalGreaterThanADaySelector);
  const kpiMetrics: any = useSelector(kpiDimensionMetricsSelector);
  const supportedMetricsStore = useSelector(supportedMetricsSelector);
  const benchmarkFlag = useSelector(benchmarkSelector);
  const advancedBenchmarkFlag = useSelector(advanceBenchmarkSelector);
  const allDimensions = useSelector(dimensionsListSelector);
  const appliedFilter = useSelector(filterSelector);

  const benchmarkSupportedMetrics = useMemo(
    () => getBenchmarkSupportedMetrics(supportedMetricsStore),
    [supportedMetricsStore],
  );

  const filterInfo = useMemo(() => getFilterInfo(appliedFilter), [appliedFilter]);

  const benchmarkSupportedDimensions = useMemo(
    () => getBenchmarkSupportedDimensions(benchmarkFlag, advancedBenchmarkFlag, allDimensions),
    [benchmarkFlag, advancedBenchmarkFlag, allDimensions],
  );

  const kpiMetricList = useMemo(
    () =>
      getKpiMetricList(
        kpiMetrics,
        showImpactedDevices,
        isIntervalGreaterThanADay,
        benchmarkSupportedMetrics,
      ),
    [kpiMetrics, showImpactedDevices, isIntervalGreaterThanADay, benchmarkSupportedMetrics],
  );

  const allMetricValues = useMemo(() => kpiMetricList.map((m) => m.value), [kpiMetricList]);
  const allDimensionValues = useMemo(
    () => benchmarkSupportedDimensions.map((d) => d.value),
    [benchmarkSupportedDimensions],
  );
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([]);
  const [selectedDimensions, setSelectedDimensions] = useState<string[]>([]);
  const [benchmarkType, setBenchmarkType] = useState<'good' | 'best'>(BENCHMARK_TYPES.GOOD);
  const [percentile, setPercentile] = useState<number>(PERCENTILE_TYPES.P95);

  const { exportBenchmarkData, isExporting, progress, error: exportError } = useBenchmarkExport();

  useEffect(() => {
    if (visible && allMetricValues.length > 0) {
      const initialDimensions = getInitialDimensions(allDimensionValues, MAX_DIMENSIONS_ALLOWED);
      setSelectedMetrics(allMetricValues);
      setSelectedDimensions(initialDimensions);
      form.setFieldsValue({
        metrics: allMetricValues,
        dimensions: initialDimensions,
        benchmarkType: BENCHMARK_TYPES.GOOD,
        percentile: PERCENTILE_TYPES.P95,
      });
      setBenchmarkType(BENCHMARK_TYPES.GOOD);
      setPercentile(PERCENTILE_TYPES.P95);
    }
  }, [visible, allMetricValues, allDimensionValues, form, filterInfo.hasCountryInFilter]);

  const handleMetricToggle = (metricValue: string) => {
    setSelectedMetrics((prev) => {
      const newSelection = prev.includes(metricValue)
        ? prev.filter((m) => m !== metricValue)
        : [...prev, metricValue];
      form.setFieldsValue({ metrics: newSelection });
      return newSelection;
    });
  };

  const handleDimensionToggle = (dimensionValue: string) => {
    setSelectedDimensions((prev) => {
      let newSelection: string[];

      if (prev.includes(dimensionValue)) {
        // Remove dimension
        newSelection = prev.filter((d) => d !== dimensionValue);
      } else {
        // Add dimension if under limit
        if (prev.length >= MAX_DIMENSIONS_ALLOWED) {
          message.warning(MESSAGES.MAX_DIMENSIONS_WARNING);
          return prev;
        }
        newSelection = [...prev, dimensionValue];
      }

      form.setFieldsValue({ dimensions: newSelection });
      return newSelection;
    });
  };

  const handleBenchmarkTypeChange = (e: any) => {
    const value = e.target.value;
    setBenchmarkType(value);
    form.setFieldsValue({ benchmarkType: value });
  };

  const handlePercentileChange = (e: any) => {
    const value = e.target.value;
    setPercentile(value);
    form.setFieldsValue({ percentile: value });
  };

  const handleExport = async () => {
    try {
      await form.validateFields();
      message.loading({ content: MESSAGES.EXPORT_PREPARING, key: 'export', duration: 0 });

      const { benchmarkType: formBenchmarkType, percentile: formPercentile } =
        form.getFieldsValue();

      const result = await exportBenchmarkData({
        dimensions: selectedDimensions,
        metrics: selectedMetrics,
        benchmarkType: formBenchmarkType ?? benchmarkType,
        percentile: formPercentile ?? percentile,
      });

      if (result.success) {
        message.success({
          content: MESSAGES.EXPORT_SUCCESS,
          key: 'export',
          duration: 5,
        });
        onClose();
      } else {
        message.error({
          content: `${MESSAGES.EXPORT_FAILED}: ${result.error}`,
          key: 'export',
          duration: 5,
        });
      }
    } catch (err) {
      message.error({
        content: MESSAGES.VALIDATION_ERROR,
        key: 'export',
        duration: 3,
      });
    }
  };

  useEffect(() => {
    if (exportError) {
      message.error({ content: exportError, duration: 5 });
    }
  }, [exportError]);

  const footerContent = (
    <>
      {isExporting && (
        <Progress percent={progress} size="small" status="active" style={{ marginBottom: 16 }} />
      )}
      <Space style={{ float: 'right' }}>
        <Button onClick={onClose} disabled={isExporting}>
          Cancel
        </Button>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={handleExport}
          disabled={selectedMetrics.length === 0 || selectedDimensions.length === 0}
          loading={isExporting}
        >
          {isExporting ? 'Exporting...' : 'Export'}
        </Button>
      </Space>
    </>
  );

  useEffect(() => {
    if (onFooterRender) {
      onFooterRender(footerContent);
    }
  }, [isExporting, progress, selectedMetrics.length, selectedDimensions.length]);

  return (
    <ContentArea>
      {filterInfo.hasCountryInFilter ? (
        <Alert
          message={
            <Space direction="vertical" size="small" style={{ width: '100%' }}>
              <Text strong>Current Filter: {filterInfo.filterName}</Text>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Country is present in the applied filter. Benchmark data will be exported for{' '}
                <strong>all selected dimensions</strong>.
              </Text>
            </Space>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
      ) : (
        <Alert
          message="Select a Country as filter to get benchmark for other supported dimensions."
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form form={form} layout="vertical">
        <Form.Item label="Benchmark Type" name="benchmarkType">
          <Radio.Group onChange={handleBenchmarkTypeChange} value={benchmarkType}>
            <Space>
              <Radio value={BENCHMARK_TYPES.GOOD}>Conviva Good</Radio>
              <Radio value={BENCHMARK_TYPES.BEST}>Conviva Best</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Divider />

        <Form.Item label="Percentile" name="percentile">
          <Radio.Group onChange={handlePercentileChange} value={percentile}>
            <Space>
              <Radio value={PERCENTILE_TYPES.P50}>Median (P50)</Radio>
              <Radio value={PERCENTILE_TYPES.P95}>Benchmark (P95)</Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Divider />

        <Form.Item label={`Dimensions (Select up to ${MAX_DIMENSIONS_ALLOWED})`} name="dimensions">
          <Row gutter={[8, 8]}>
            {benchmarkSupportedDimensions.map((dimension) => (
              <Col span={12} key={dimension.value}>
                <Checkbox
                  checked={selectedDimensions.includes(dimension.value)}
                  onChange={() => handleDimensionToggle(dimension.value)}
                >
                  {dimension.label}
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Form.Item>

        <Divider />

        <Form.Item label="Metrics" name="metrics">
          <Row gutter={[8, 8]}>
            {kpiMetricList.map((metric) => (
              <Col span={12} key={metric.value}>
                <Checkbox
                  checked={selectedMetrics.includes(metric.value)}
                  onChange={() => handleMetricToggle(metric.value)}
                >
                  {metric.label}
                </Checkbox>
              </Col>
            ))}
          </Row>
        </Form.Item>
      </Form>
    </ContentArea>
  );
};

export default BenchmarkExportDrawer;
